import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Divider,
  Switch,
  FormControlLabel,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDarkMode } from '../context/DarkModeContext';

export const SettingsPage = () => {
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [localDarkMode, setLocalDarkMode] = useState(false); // default to false instead of null
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [userId, setUserId] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true); // true initially
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const hasFetchedRef = useRef(false);
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useDarkMode();


  // Fetch authenticated user info (only once)
useEffect(() => {
  let isMounted = true;

  axios
    .get('https://ipms.insollex.com/api/user', { withCredentials: true })
    .then((res) => {
      if (isMounted) {
        setUserId(res.data.id);
        setError('');
      }
    })
    .catch(() => isMounted && setError('User not logged in'));

  return () => {
    isMounted = false;
  };
}, []);

// Fetch user settings only ONCE when userId is available
useEffect(() => {
  if (!userId || hasFetchedRef.current) return;

  hasFetchedRef.current = true; // Mark as fetched before the async call to prevent race conditions
  setSettingsLoading(true);

  axios
    .get('https://ipms.insollex.com/api/settings', { withCredentials: true })
    .then((res) => {
      const toBoolean = (v) => v === true || v === 'true';
    
      setNotificationsEnabled(toBoolean(res.data.notificationsEnabled));
    
      // Sync darkMode in context with server value
      if (typeof res.data.darkMode === 'boolean' && res.data.darkMode !== darkMode) {
        toggleDarkMode(res.data.darkMode);
      }
    
      setError('');
    })
    .catch(() => {
      setError('Failed to load settings.');
    })
    .finally(() => {
      setSettingsLoading(false);
    });
}, [userId]);


const handleSave = async () => {
  if (!userId) {
    setError('User not logged in');
    return;
  }

  try {
    setSaving(true);

    await axios.post(
      'https://ipms.insollex.com/api/settings',
      {
        notificationsEnabled,
        darkMode,
      },
      { withCredentials: true }
    );

    setSnackbar({
      open: true,
      message: 'Settings saved successfully!',
      severity: 'success',
    });
    setError('');
  } catch {
    setSnackbar({
      open: true,
      message: 'Failed to save settings.',
      severity: 'error',
    });
  } finally {
    setSaving(false);
  }
};


  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ p: 3, maxWidth: 600, margin: '0 auto' }}>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>

      <Divider sx={{ my: 2 }} />

      <FormControlLabel
  control={
    <Switch
      checked={darkMode}
      disabled={settingsLoading}
      onChange={(e) => toggleDarkMode(e.target.checked)}
    />
  }
  label="Enable Dark Mode"
/>

      <Divider sx={{ my: 2 }} />

      <Button
        variant="contained"
        onClick={handleSave}
        disabled={saving || settingsLoading}
        sx={{ mt: 2 }}
      >
        {saving ? 'Saving...' : 'Save Changes'}
      </Button>

      <Button
        variant="outlined"
        onClick={() => navigate(-1)}
        sx={{ mt: 2, ml: 2 }}
        disabled={saving}
      >
        Cancel
      </Button>

      {error && (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsPage;
